import React, { useState, useEffect } from 'react';
import PermissionGate from 'global/components/permission-gate';
import { useChartSettings } from 'global/hooks/useChartSettings';
import { Text } from '../../../global/components/text';
import { SortableList } from 'global/components/sortable-list';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { Row } from '../../../global/components/row';
import { IconButton } from '../../../global/components/Button';
import { get, size, flatten } from '../../../global/lib/lodash';
import { ManageDataContainer, TierBox } from './styles';
import { EditChartModal } from '../../../global/components/Charting';
import ConfirmModal from '../../../global/components/confirm';
import { cleanCopy } from '../../../global/utils';
import { CenterLoader } from '../../../global/styles';
import { FilterChips } from '../../../global/components/filterchips';
import { useFilter } from '../../../global/hooks/useFilter';

export const ManageData = PermissionGate(props => {
  const [state, setState] = useState({
    tiers: [],
  })

  const [editModal, setEditModal] = useState({
    open: false,
    onClose: () => {
      setEditModal(s => ({ ...s, open: false }));
    }
  })

  const [confirmModal, setConfirmModal] = useState()

  const usergroups = useRetrieveData({ url: `/user-groups` }, { build: ['userGroups'] });
  const chartSettings = useChartSettings({ loadCharts: false, isAdmin: true });
  const { user } = useFilter("admin.data");

  useEffect(() => {
    const tiers = [
      { 
        title: 'Builder Charts',
        subtitle: 'These charts are the default display for users of the specified User Group with access to the whole company.',
        none: 'Builders',
        key: 'builder',
        charts: [],
      },
      { 
        title: 'Division Charts',
        subtitle: 'These charts are the default display for users groups when they have access to a division or for any user when their site location is set to a division.',
        none: 'Divisions',
        key: 'division',
        charts: [],
      },
      { 
        title: 'Community Charts',
        subtitle: 'These charts are the default display for users groups when their site location is set to a community.',
        none: 'Communities',
        key: 'community',
        charts: [],
      },
      { 
        title: 'Lot Charts',
        subtitle: 'These charts are the default display for users groups when their site location is set to a lot.',
        none: 'Lots',
        key: 'lot',
        charts: [],
      }
    ]

    chartSettings.charts.map(c => {
      if (user.realm && !c.realms.find(r => r.id == user.realm.id)) { return }

      tiers.forEach(t => {
        if (c.realmTier === t.key) {
          t.charts = [...t.charts, c];
        }
      })
    })

    setState(s => ({ ...s, tiers }))
  }, [chartSettings.charts, user.realm])

  const onSort = (items) => {
    const newCharts = [];

    const ids = items.map(i => i.id);
    const newTiers = [...state.tiers].map(t => {
      const { charts } = t;

      const ismatch = charts.find(c => ids.includes(c.id));

      if (ismatch) {
        t.charts = items;
      } 

      newCharts.push(t.charts)

      return t;
    })

    const order = flatten(cleanCopy(newCharts)).map((c, i) => {
      return {
        ...c,
        content: undefined,
        key: undefined,
        order: i,
      }
    })

    chartSettings.updateCharts(order);

    setState(s => ({ ...s, tiers: newTiers }));
  }

  const editChart = chart => {
    setEditModal(s => ({
      ...s,
      title: 'EDIT CHART',
      open: true,
      chart,
      onSubmit: async vals => {
        await chartSettings.createAndSave({ chart: { ...chart, ...vals, content: undefined, key: undefined, } })
        s.onClose();
      }
    }))
  }

  const deleteChart = chart => {
    const close = () => {
      setConfirmModal();
    }
    setConfirmModal({
      title: 'Delete Chart',
      content: `Are you sure you want to delete chart ${chart.name}?`,
      onOk: async () => {
        await chartSettings.deleteChart(chart);
        close()
      },
      onCancel: () => {
        close();
      }
    })
  }

  const renderRow = (chart) => {
    const ugroups = chart.userGroups.map(u => get(usergroups.data.find(ug => ug.id == u.id), 'name')).join(', ');
    const realms = !size(chart.realms) ? 'Global' : (chart.realms || []).map(r => get(r, 'name')).join(', ');

    return (
      <Row fullWidth style={{ paddingTop: 13 }}>
        <div style={{ width: '30%' }}>
          <Text type="title_medium">{chart.name}</Text>
          <Text type="label_small">{chart.description}</Text>
        </div>

        <div style={{ width: '30%' }}>{realms}</div>
        <div style={{ width: '30%' }}>{ugroups}</div>
        
        <Row style={{ width: '10%', marginLeft: 'auto', justifyContent: 'flex-end' }}>
          <IconButton size="small" icon="edit" variant="text" onClick={() => editChart(chart)} />
          <IconButton size="small" icon="delete" variant="text" onClick={() => deleteChart(chart)} />
        </Row>
      </Row>
    )
  }

  const renderTier = (tier, i) => {
    return (
      <TierBox key={i}>
        <div><Text type="title_medium">{tier.title}</Text></div>
        <div><Text type="label_small">{tier.subtitle}</Text></div>
        {!size(tier.charts) && <Text italic type="label_small">{`There are no default charts for ${tier.none}. Go to the `}<a href="/reports/data">Assessments Data page</a>{` to create some.`}</Text>}
        {!!size(tier.charts) && <SortableList verticalOnly items={tier.charts.map((c, _i) => ({ ...c, key: `${_i}`, content: renderRow(c) }))} onSort={onSort} />}
      </TierBox>
    )
  }

  return (
    <ManageDataContainer>
      <Text type="title_large">Assessment Charts</Text>
      <FilterChips id="admin.data" />
      <div>
        {chartSettings.loading ? <CenterLoader /> : state.tiers.map(renderTier)}
      </div>

      <EditChartModal {...editModal} />
      <ConfirmModal {...confirmModal} />
    </ManageDataContainer>
  )
})